import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useLocation } from 'react-router-dom'
import { Text, Stack, Box, Columns, Column, Button, IconRocket } from '@machobear/design-system'
import { useDialogControllers } from '@machobear/design-system'
import { useState } from 'react'
import AreaTitle from '@/components/area-title'
import { Breadcrumbs } from '@/components/breadcrumbs'
import SearchBar from '@/components/search-bar'
import { colors, breakpoints, structure, fontWeights } from '@/styles'
import useMediaQuery from '@/hooks/use-media-query'
import SponsorDialogs from '@/features/SponsoredListing/components/sponsor-dialogs'
import { SponsoredListingProvider } from '@/features/SponsoredListing/modals/sponsor-wizard'
import PageTitle from '@/components/page-title'
import useProfile from '@/hooks/use-profile'

const PageHeader = styled.div`
  top: ${props => props.top ?? 0}px;
  position: sticky;
  width: 100%;
  z-index: 20;
  background-color: ${colors.white};
  height: ${props => props.$height}px;
  padding: ${props => props.padding ?? '0'};

  @media only screen and (max-width: ${breakpoints.desktop}) {
    height: 122px;
  }

  @media only screen and (max-width: ${breakpoints.desktopSmall}) {
    height: 97px;
  }

  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    box-shadow: 0 2px 8px 0 rgba(146, 146, 146, 0.69);
    height: 64px;
    top: ${structure.header.heightMobile}px;
  }
`

const PageHeaderOutter = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
`

const PageHeaderInner = styled.div`
  max-width: ${props => props.maxWidth ?? '1130px'};
  width: calc(100% - 100px);
  margin: 0 50px;
  padding: ${props => props.padding ?? '45px 0 45px 0'};
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  background: ${colors.white};

  @media only screen and (max-width: ${breakpoints.desktop}) {
    margin: 0 30px;
    width: calc(100% - 60px);
    padding: 30px 0 42px 0;
  }

  @media only screen and (max-width: ${breakpoints.desktopSmall}) {
    margin: 0 30px;
    padding: 24px 0 32px 0;
  }

  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin: 0 30px;
    padding: 12px 0;
  }

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    justify-content: center;
    margin: 0 10px;
  }
`

const BreadcrumbsWrapper = styled.div`
  @media only screen and (max-width: ${breakpoints.tabletLandscape}) {
    margin: 0 25px;
  }
`

const HeaderTitle = styled.h1`
  font-size: 30px;
  color: ${colors.regular};
  font-weight: ${fontWeights.bold};
  line-height: 41px;
  margin: 0;
  text-overflow: ellipsis;
  overflow: hidden;

  @media only screen and (max-width: ${breakpoints.tabletLandscape}) {
    display: none;
  }
`

const FiltersWrapper = styled.div`
  display: flex;
  justify-content: space-between;

  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    flex-direction: column;
    align-items: center;
    gap: 12px 0;
    width: 100%;
  }
`

const SearchInner = styled.div`
  width: ${props => (props.buttonComponent ? '52%' : '45%')};
  > div,
  > a,
  > button {
    white-space: nowrap;
  }
  @media only screen and (max-width: 991px) {
    width: 100%;
    > a,
    > button {
      display: none;
    }
  }
`

const SectionHeading = styled.h1`
  font-size: 30px;
  color: ${colors.regular};
  font-weight: ${fontWeights.bold};
  line-height: 41px;
  margin: 0;

  @media only screen and (max-width: ${breakpoints.tabletLandscape}) {
    font-size: 25px;
    line-height: 34px;
  }
`

const ListingApplicationsHeader = ({
  height = 126,
  title,
  description = '',
  onSearch,
  breadcrumbs,
  maxWidth,
  listingId
}) => {
  const { t } = useTranslation()
  const location = useLocation()
  const profileData = useProfile()
  const pathName = location.pathname
  const isSettings = pathName.includes('manage')
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const displayBreadcrumbs = useMediaQuery(`(max-width: ${breakpoints.desktopSmall})`)
  const displayHeading = useMediaQuery(`(max-width: 991px)`)
  const headerHeight = isSettings ? 200 : !isMobile ? height : 140
  const isBillingUserValid = profileData?.billing_user_valid
  const [searchString, setSearchString] = useState('')

  const { visible, showModal, closeModal } = useDialogControllers({
    billingUser: false,
    sponsoredListing: false,
    nextSteps: false
  })

  const handleBoostListing = () => {
    if (isBillingUserValid) {
      showModal('sponsoredListing')()
    } else {
      showModal('billingUser')()
    }
  }

  const BoostButton = () => {
    return (
      <Button
        variant="solid"
        state="default"
        size="standard"
        label="Boost Listing"
        icon={<IconRocket />}
        iconPosition="leading"
        onClick={handleBoostListing}
        id={`applications-table-sponsor-listing-boost-click-${listingId}`}
      >
        {t('l.application.table.header.boost_listing', 'Boost Listing')}
      </Button>
    )
  }

  const ButtonComponent = BoostButton

  const onSearchWithScroll = value => {
    const output = onSearch(value)
    if (typeof window !== 'undefined') window.scrollTo(0, 0)
    return output
  }

  const BreadcrumbsComponent = () => {
    return (
      <BreadcrumbsWrapper>
        <Breadcrumbs items={breadcrumbs} />
      </BreadcrumbsWrapper>
    )
  }

  return (
    <PageHeader
      $height={headerHeight}
      top={isSettings ? 0 : 64}
      padding={isSettings ? '0 0 60px' : undefined}
    >
      {breadcrumbs && !displayBreadcrumbs ? (
        <PageTitle>
          <BreadcrumbsComponent />
        </PageTitle>
      ) : (
        <PageTitle> {t('l.listing.application.applications.text', 'Applications')}</PageTitle>
      )}
      <PageHeaderOutter>
        <PageHeaderInner padding={isSettings ? '0' : undefined} maxWidth={maxWidth}>
          {isSettings ? (
            <Stack space="xxsmall">
              {!isMobile && (
                <Box>
                  <AreaTitle textAlign="left" top="40" bottom="10">
                    {title}
                  </AreaTitle>

                  <Text tone="secondary" size="small">
                    {description}
                  </Text>
                </Box>
              )}

              <FiltersWrapper>
                <SectionHeading>
                  {t('l.listing.application.applications.text', 'Applications')}
                </SectionHeading>

                <SearchInner>
                  <ButtonComponent />
                  <SearchBar
                    placeholder={t('common.search', 'Search')}
                    onSearch={onSearchWithScroll}
                    fullWidth
                    searchString={searchString}
                    setSearchString={setSearchString}
                  />
                </SearchInner>
              </FiltersWrapper>
            </Stack>
          ) : (
            <>
              <HeaderTitle>{title}</HeaderTitle>
              <FiltersWrapper>
                {!displayHeading && (
                  <SectionHeading>
                    {t('l.listing.application.applications.text', 'Applications')}
                  </SectionHeading>
                )}

                <SearchInner buttonComponent>
                  {!isMobile ? (
                    <Box
                      height="touchable"
                      display="flex"
                      gap="gutter"
                      width="full"
                      justifyContent="spaceBetween"
                    >
                      {!displayHeading ? (
                        <>
                          <Box style={{ width: '200px' }}>
                            <ButtonComponent />
                          </Box>

                          <SearchBar
                            placeholder={t('common.search', 'Search')}
                            onSearch={onSearchWithScroll}
                            fullWidth
                            searchString={searchString}
                            setSearchString={setSearchString}
                          />
                        </>
                      ) : (
                        <>
                          <Box style={{ width: '200px' }}>
                            <ButtonComponent />
                          </Box>
                          <Box style={{ width: '45%' }}>
                            <SearchBar
                              placeholder={t('common.search', 'Search')}
                              onSearch={onSearchWithScroll}
                              fullWidth
                              searchString={searchString}
                              setSearchString={setSearchString}
                            />
                          </Box>
                        </>
                      )}
                    </Box>
                  ) : (
                    <Columns space="small">
                      <Column width="5/5">
                        <SearchBar
                          placeholder={t('common.search', 'Search')}
                          onSearch={onSearchWithScroll}
                          fullWidth
                          searchString={searchString}
                          setSearchString={setSearchString}
                        />
                      </Column>
                    </Columns>
                  )}
                </SearchInner>
              </FiltersWrapper>
            </>
          )}
        </PageHeaderInner>
      </PageHeaderOutter>
      <SponsoredListingProvider initialStep={0} initialValues={{}} isCreateListing={false}>
        <SponsorDialogs
          listingId={listingId}
          visible={visible}
          showModal={showModal}
          closeModal={closeModal}
        />
      </SponsoredListingProvider>
    </PageHeader>
  )
}

export default ListingApplicationsHeader
